import { useCallback, useEffect, useRef, useState } from 'react';
import { getFactoryExpert, saveFactoryExpert, type FactoryConfig, type FactoryExpert } from '../../lib/factory-experts-api';
import { HttpJsonError } from '../../lib/http-json';

export type SaveState = 'saved' | 'pending' | 'saving' | 'failed' | 'conflict';
export function useFactoryDraft(initial: FactoryExpert) {
  const [expert, setExpert] = useState(initial);
  const [config, setConfig] = useState(initial.config);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [saveError, setSaveError] = useState('');
  const server = useRef(initial);
  const local = useRef(initial.config);
  const inFlight = useRef<Promise<FactoryExpert> | null>(null);
  const paused = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const edit = useCallback((update: (value: FactoryConfig) => FactoryConfig) => {
    const next = update(local.current);
    local.current = next;
    setConfig(next);
    if (!paused.current) setSaveState('pending');
  }, []);

  const flush = useCallback(async (): Promise<FactoryExpert> => {
    if (paused.current) throw new Error('请先处理保存失败或其他设备的修改。');
    if (inFlight.current) return inFlight.current;
    const pending = (async () => {
      try {
        while (JSON.stringify(local.current) !== JSON.stringify(server.current.config)) {
          const submitted = local.current;
          if (alive.current) setSaveState('saving');
          const saved = await saveFactoryExpert(server.current.id, server.current.revision, submitted);
          server.current = saved;
          if (alive.current) setExpert(saved);
          if (local.current === submitted) {
            local.current = saved.config;
            if (alive.current) setConfig(saved.config);
          }
        }
        if (alive.current) { setSaveState('saved'); setSaveError(''); }
        return server.current;
      } catch (error) {
        paused.current = true;
        if (alive.current) {
          setSaveState(error instanceof HttpJsonError && error.status === 409 ? 'conflict' : 'failed');
          setSaveError(error instanceof Error ? error.message : '保存失败，请重试。');
        }
        throw error;
      }
    })();
    inFlight.current = pending;
    try { return await pending; } finally { inFlight.current = null; }
  }, []);

  useEffect(() => {
    if (saveState !== 'pending') return;
    const timer = window.setTimeout(() => { void flush().catch(() => undefined); }, 650);
    return () => window.clearTimeout(timer);
  }, [config, saveState, flush]);

  useEffect(() => {
    const preventLostEdit = (event: BeforeUnloadEvent) => {
      if (JSON.stringify(local.current) !== JSON.stringify(server.current.config)) {
        event.preventDefault(); event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', preventLostEdit);
    return () => window.removeEventListener('beforeunload', preventLostEdit);
  }, []);

  const accept = useCallback((next: FactoryExpert) => {
    server.current = next; local.current = next.config; paused.current = false;
    setExpert(next); setConfig(next.config); setSaveState('saved'); setSaveError('');
  }, []);
  const retry = useCallback(async () => { paused.current = false; return flush(); }, [flush]);
  const resolveConflict = useCallback((remote: FactoryExpert, merged: FactoryConfig) => {
    server.current = remote; local.current = merged; paused.current = false;
    setExpert(remote); setConfig(merged); setSaveError(''); setSaveState('pending');
  }, []);
  const loadRemote = useCallback(() => getFactoryExpert(server.current.id), []);
  return { expert, config, edit, flush, accept, saveState, saveError, retry, loadRemote, resolveConflict };
}
